import { BugOutlined } from "@ant-design/icons";
import { Alert, Button, Form, Input, Tooltip, message } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { USER_FEEDBACK_API } from "../api/user-feedback.js";
import { http } from "../api/client.js";
import { FancyModal } from "./ui/FancyModal.js";
import {
	FeedbackDescription,
	type FeedbackDescriptionValue,
} from "./FeedbackDescription.js";

export function FeedbackButton() {
	const { t } = useTranslation("feedback");
	const location = useLocation();
	const [open, setOpen] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [reading, setReading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [form] = Form.useForm<{
		title: string;
		description: FeedbackDescriptionValue;
	}>();
	const pageUrl = `${location.pathname}${location.search}`;
	const submit = async () => {
		if (reading || submitting) return;
		let values: { title: string; description: FeedbackDescriptionValue };
		try {
			values = await form.validateFields();
		} catch (cause) {
			const first = (
				cause as { errorFields?: Array<{ name: string[]; errors: string[] }> }
			)?.errorFields?.[0];
			setError(first?.errors?.[0] ?? t("descriptionRequired"));
			if (first?.name)
				form.scrollToField(first.name, { block: "center", behavior: "smooth" });
			return;
		}
		setSubmitting(true);
		setError(null);
		try {
			await http.post(
				"/user-feedback",
				{
					title: values.title,
					body: values.description.text,
					images: values.description.images,
					pageUrl,
				},
				USER_FEEDBACK_API,
			);
			form.resetFields();
			setOpen(false);
			message.success(t("submitted"));
		} catch (cause) {
			const responseMessage =
				cause && typeof cause === "object" && "response" in cause
					? (cause as { response?: { data?: { message?: unknown } } }).response
							?.data?.message
					: undefined;
			setError(
				typeof responseMessage === "string"
					? responseMessage
					: t("submitFailed"),
			);
		} finally {
			setSubmitting(false);
		}
	};
	return (
		<>
			<Tooltip title={t("title")}>
				<Button
					type="text"
					className="crest-header-action"
					aria-label={t("title")}
					icon={<BugOutlined />}
					onClick={() => {
						setError(null);
						setOpen(true);
					}}
				/>
			</Tooltip>
			<FancyModal
				title={t("title")}
				open={open}
				okText={t("submit")}
				cancelText={t("cancel")}
				footer={(buttons) => (
					<>
						{error && (
							<Alert
								type="error"
								showIcon
								message={error}
								style={{ marginBottom: 12, textAlign: "left" }}
							/>
						)}
						{buttons}
					</>
				)}
				confirmLoading={submitting}
				okButtonProps={{ disabled: reading }}
				cancelButtonProps={{ disabled: submitting || reading }}
				closable={!submitting && !reading}
				maskClosable={!submitting && !reading}
				keyboard={!submitting && !reading}
				onOk={() => void submit()}
				onCancel={() => {
					if (!submitting && !reading) setOpen(false);
				}}
			>
				<Form
					form={form}
					layout="vertical"
					requiredMark={false}
					disabled={submitting}
					initialValues={{ description: { text: "", images: [] } }}
				>
					<Form.Item label={t("page")}>
						<Input value={pageUrl} readOnly />
					</Form.Item>
					<Form.Item
						name="title"
						label={t("subject")}
						rules={[
							{
								required: true,
								whitespace: true,
								message: t("subjectRequired"),
							},
							{ max: 120, message: t("subjectMax") },
						]}
					>
						<Input placeholder={t("subjectPlaceholder")} maxLength={120} />
					</Form.Item>
					<Form.Item
						name="description"
						label={t("description")}
						rules={[
							{
								validator: (_, value?: FeedbackDescriptionValue) =>
									value &&
									(value.text.trim() || value.images.length) &&
									value.text.length <= 4000
										? Promise.resolve()
										: Promise.reject(new Error(t("descriptionRequired"))),
							},
						]}
					>
						<FeedbackDescription
							disabled={submitting}
							onBusyChange={setReading}
						/>
					</Form.Item>
				</Form>
			</FancyModal>
		</>
	);
}
